import { MATCH_PATTERN_ERROR_MESSAGE } from '@src/emp-3.0/constants/common';

/**
 * Checks if the provided input string is free from potential SQL injection patterns.
 *
 * This function uses a case-insensitive regular expression to detect suspicious SQL keywords
 * or fragments that are commonly used in SQL injection attacks. The regex matches patterns such as:
 * - "insert into"
 * - "delete from"
 * - "drop table"
 * - "drop database"
 * - "update ... set" (with any content in between)
 * - "alter table"
 * - "truncate table"
 * - "create table"
 * - "or 1=1" (a common always-true condition used in SQL injection)
 *
 * @param input - The input string to be checked.
 * @returns {boolean} True if the input does not contain any suspicious SQL patterns, otherwise false.
 */
export function isSecureInput(input: string): boolean {
  const regex =
    /\b(insert\s+into|delete\s+from|drop\s+table|drop\s+database|update\s+.+\s+set|alter\s+table|truncate\s+table|\bcreate\s+table\b|\bor\s+1\s*=\s*1\b)/i;
  return !regex.test(input);
}

export const isOnlyStars = (str: string = '') => {
  return /^\*+$/.test(str);
};

/**
 * Check if the value contains only legal characters
 * @param value The string to validate
 * @returns true if value is valid (legal), false otherwise (illegal)
 */
export const isLegalString = (value: string): boolean => {
  if (value === '') return true;

  const regex =
    /^[\u4e00-\u9fa5\w\s|()[\]\-/.{}'"#$%^&!;?=@:*,+~áàâãéêíóôõúüñçÁÀÂÃÉÊÍÓÔÕÚÜÑÇ\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]+$/u;
  return regex.test(value);
};

/**
 * Combined validator for length and character legality
 * @param value The string to validate
 * @param options Validation options
 * @returns ValidationResult
 */
export const VALIDATE_SECURE_INPUT_MESSAGE =
  'The SQL statement is not permitted in this field. Please try a different one.';
export const validateString = (value?: string | null, options: any = {}) => {
  const { minLength, maxLength, validateLegality = true, matchPattern, validateSecure } = options;
  if (!value)
    return {
      failed: false,
      message: 'success',
    };

  // Length validation
  if (minLength !== undefined && value.length < minLength) {
    return {
      failed: true,
      message: `must contain at least ${minLength} characters.`,
    };
  }

  if (maxLength !== undefined && value.length > maxLength) {
    return {
      failed: true,
      message: `exceeds the maximum character limit (${maxLength})`,
    };
  }

  // Legality validation
  // Character legality validation
  if (validateLegality && !isLegalString(value)) {
    return {
      failed: true,
      message:
        "This field must contain at least one language character, letter, number, underscore, whitespace, or special character such as | ( ) [ ] - / . { } ' ” # $ % ^ & ! ; ? = @ : * , + ~.",
    };
  }

  // SQL injection risk validation
  if (validateLegality && !isSecureInput(value)) {
    return {
      failed: true,
      message: VALIDATE_SECURE_INPUT_MESSAGE,
    };
  }
  if (validateSecure && !isSecureInput(value)) {
    return {
      failed: true,
      message: VALIDATE_SECURE_INPUT_MESSAGE,
    };
  }

  if (matchPattern && isOnlyStars(value)) {
    return {
      failed: true,
      message: MATCH_PATTERN_ERROR_MESSAGE,
    };
  }

  return {
    failed: false,
    message: 'success',
  };
};
